import React, { useState, useEffect } from 'react';
import { Activity, User, Shield, Key, Package, Clock, Filter, Download, RefreshCw } from 'lucide-react';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { Badge } from '../../components/common/Badge';

interface ActivityEntry {
  id: string;
  timestamp: string;
  user: {
    id: number;
    email: string;
    full_name?: string;
  };
  action: string;
  resource: string;
  resource_id?: string;
  details?: string;
  ip_address?: string;
  user_agent?: string;
  status: 'success' | 'failure' | 'warning';
}

interface ActivityLogProps {
  onNavigate: (page: string) => void;
}

export const ActivityLog: React.FC<ActivityLogProps> = () => {
  const [activities, setActivities] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState({
    action: 'all',
    resource: 'all',
    status: 'all',
    timeRange: '24h'
  });

  // Mock data for demonstration
  useEffect(() => {
    fetchActivities();
  }, [filter]);

  const fetchActivities = async () => {
    try {
      setLoading(true);

      // Construct query params
      const params = new URLSearchParams();
      if (filter.timeRange !== 'all') params.append('time_range', filter.timeRange);
      if (filter.action !== 'all') params.append('action', filter.action);
      if (filter.resource !== 'all') params.append('resource', filter.resource);
      if (filter.status !== 'all') params.append('status', filter.status);

      // Get token from localStorage
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`/api/activity/?${params}`, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch activities');
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        const formattedActivities: ActivityEntry[] = data.data.map((item: any) => ({
          id: item.id,
          timestamp: item.timestamp,
          user: item.user || { id: 0, email: 'System', full_name: 'System' },
          action: item.action,
          resource: item.resource,
          resource_id: item.resource_id,
          resource_name: item.resource_name,
          details: item.details,
          ip_address: item.ip_address,
          user_agent: item.user_agent,
          status: item.status || 'success',
        }));
        setActivities(formattedActivities);
      } else {
        // Use mock data as fallback
        const mockActivities: ActivityEntry[] = [
        {
          id: '1',
          timestamp: new Date().toISOString(),
          user: { id: 1, email: 'admin@example.com', full_name: 'Admin User' },
          action: 'CREATE',
          resource: 'organization',
          resource_id: 'org-123',
          details: 'Created organization "New Company"',
          ip_address: '192.168.1.1',
          status: 'success'
        },
        {
          id: '2',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          user: { id: 2, email: 'user@example.com', full_name: 'Test User' },
          action: 'LOGIN',
          resource: 'auth',
          details: 'User logged in via Google OAuth',
          ip_address: '192.168.1.2',
          status: 'success'
        },
        {
          id: '3',
          timestamp: new Date(Date.now() - 7200000).toISOString(),
          user: { id: 1, email: 'admin@example.com', full_name: 'Admin User' },
          action: 'UPDATE',
          resource: 'user',
          resource_id: 'user-456',
          details: 'Updated user permissions',
          ip_address: '192.168.1.1',
          status: 'success'
        },
        {
          id: '4',
          timestamp: new Date(Date.now() - 10800000).toISOString(),
          user: { id: 3, email: 'another@example.com', full_name: 'Another User' },
          action: 'DELETE',
          resource: 'product',
          resource_id: 'prod-789',
          details: 'Deleted product "Old Item"',
          ip_address: '192.168.1.3',
          status: 'warning'
        },
        {
          id: '5',
          timestamp: new Date(Date.now() - 14400000).toISOString(),
          user: { id: 2, email: 'user@example.com', full_name: 'Test User' },
          action: 'ACCESS',
          resource: 'api',
          details: 'Unauthorized API access attempt',
          ip_address: '192.168.1.2',
          status: 'failure'
        }
        ];
        setActivities(mockActivities);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activity log');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action: string) => {
    switch (action.toUpperCase()) {
      case 'LOGIN':
      case 'LOGOUT':
        return User;
      case 'CREATE':
      case 'UPDATE':
      case 'DELETE':
        return Package;
      case 'ACCESS':
        return Key;
      default:
        return Activity;
    }
  };

  const getActionColor = (action: string) => {
    switch (action.toUpperCase()) {
      case 'CREATE':
        return 'text-green-600';
      case 'UPDATE':
        return 'text-blue-600';
      case 'DELETE':
        return 'text-red-600';
      case 'LOGIN':
        return 'text-purple-600';
      case 'ACCESS':
        return 'text-orange-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failure':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    
    if (hours < 1) {
      return `${minutes} minutes ago`;
    } else if (hours < 24) {
      return `${hours} hours ago`;
    } else {
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    }
  };

  const handleExport = () => {
    // Implement CSV export
    const csv = [
      ['Timestamp', 'User', 'Action', 'Resource', 'Details', 'Status', 'IP Address'].join(','),
      ...activities.map(a => [
        a.timestamp,
        a.user.email,
        a.action,
        a.resource,
        a.details || '',
        a.status,
        a.ip_address || ''
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `activity-log-${new Date().toISOString()}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={fetchActivities} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Activity Log</h1>
          <p className="text-gray-600 mt-1">Monitor system activity and user actions</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={fetchActivities}
            icon={RefreshCw}
            variant="outline"
            size="sm"
          >
            Refresh
          </Button>
          <Button
            onClick={handleExport}
            icon={Download}
            variant="outline"
            size="sm"
          >
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex items-center gap-4">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            value={filter.timeRange}
            onChange={(e) => setFilter({ ...filter, timeRange: e.target.value })}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="all">All Time</option>
          </select>
          <select
            value={filter.action}
            onChange={(e) => setFilter({ ...filter, action: e.target.value })}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Actions</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
            <option value="login">Login</option>
            <option value="access">Access</option>
          </select>
          <select
            value={filter.resource}
            onChange={(e) => setFilter({ ...filter, resource: e.target.value })}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Resources</option>
            <option value="organization">Organizations</option>
            <option value="user">Users</option>
            <option value="product">Products</option>
            <option value="auth">Authentication</option>
            <option value="api">API</option>
          </select>
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="success">Success</option>
            <option value="failure">Failure</option>
            <option value="warning">Warning</option>
          </select>
        </div>
      </Card>

      {/* Activity Timeline */}
      <Card>
        <div className="space-y-4">
          {activities.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No activity</h3>
              <p className="mt-1 text-sm text-gray-500">No activity has been recorded yet.</p>
            </div>
          ) : (
            activities.map((activity, index) => {
              const ActionIcon = getActionIcon(activity.action);
              return (
                <div
                  key={activity.id}
                  className={`flex items-start gap-4 ${
                    index !== activities.length - 1 ? 'border-b pb-4' : ''
                  }`}
                >
                  <div className={`p-2 rounded-lg bg-gray-50 ${getActionColor(activity.action)}`}>
                    <ActionIcon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900">
                        {activity.user.full_name || activity.user.email}
                      </span>
                      <span className={`font-medium ${getActionColor(activity.action)}`}>
                        {activity.action.toLowerCase()}
                      </span>
                      <span className="text-gray-600">
                        {activity.resource}
                        {activity.resource_id && ` #${activity.resource_id}`}
                      </span>
                      <Badge variant={getStatusVariant(activity.status)}>
                        {activity.status}
                      </Badge>
                    </div>
                    {activity.details && (
                      <p className="text-sm text-gray-600 mb-1">{activity.details}</p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatTimestamp(activity.timestamp)}
                      </div>
                      {activity.ip_address && (
                        <span>IP: {activity.ip_address}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Events</p>
              <p className="text-2xl font-bold text-gray-900">{activities.length}</p>
            </div>
            <Activity className="h-8 w-8 text-gray-400" />
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Success Rate</p>
              <p className="text-2xl font-bold text-green-600">
                {activities.length > 0
                  ? Math.round(
                      (activities.filter(a => a.status === 'success').length / activities.length) * 100
                    )
                  : 0}%
              </p>
            </div>
            <Shield className="h-8 w-8 text-green-400" />
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Users</p>
              <p className="text-2xl font-bold text-blue-600">
                {new Set(activities.map(a => a.user.id)).size}
              </p>
            </div>
            <User className="h-8 w-8 text-blue-400" />
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Failed Actions</p>
              <p className="text-2xl font-bold text-red-600">
                {activities.filter(a => a.status === 'failure').length}
              </p>
            </div>
            <Activity className="h-8 w-8 text-red-400" />
          </div>
        </Card>
      </div>
    </div>
  );
};